import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import { useEffect, useRef } from 'react';

import { VestingGraphPointStatus } from '../../../../../hooks/vesting-task/use-vesting-graph';
import {
  VESTING_GRAPH_BAR_SLOT_FILL_RATIO,
  VESTING_GRAPH_MAX_BAR_SIZE_PX,
  VESTING_GRAPH_MIN_BAR_SIZE_PX,
  VESTING_GRAPH_UNVESTED_ACTIVE_COLOR,
  VESTING_GRAPH_UNVESTED_COLOR,
  VESTING_GRAPH_VESTED_ACTIVE_COLOR,
  VESTING_GRAPH_VESTED_COLOR,
  VESTING_GRAPH_VESTED_MUTED_COLOR,
} from '../model/vesting-graph.constants';
import {
  VESTING_GRAPH_INTRO_BAR_MS,
  VESTING_GRAPH_INTRO_EASE,
  VestingGraphIntroPhase,
} from '../model/vesting-graph.intro';
import { HoveredVestingPeriod, VestingGraphSeriesPoint } from '../model/vesting-graph.types';

const BAR_TOP_RADIUS_PX = 2;

const buildVestingGraphBarPaths = ({
  drawnWidth,
  drawnX,
  height,
  scale,
  y,
}: {
  drawnWidth: number;
  drawnX: number;
  height: number;
  scale: number;
  y: number;
}) => {
  const drawnHeight = Math.max(height * scale, 0);
  // Keep the baseline on the X-axis and grow the top up — CSS scaleY would leak below it.
  const drawnY = y + height - drawnHeight;
  const radius = Math.min(BAR_TOP_RADIUS_PX, drawnWidth / 2, drawnHeight);
  const fillPath = [
    `M${drawnX},${drawnY + drawnHeight}`,
    `H${drawnX + drawnWidth}`,
    `V${drawnY + radius}`,
    `Q${drawnX + drawnWidth},${drawnY} ${drawnX + drawnWidth - radius},${drawnY}`,
    `H${drawnX + radius}`,
    `Q${drawnX},${drawnY} ${drawnX},${drawnY + radius}`,
    'Z',
  ].join(' ');
  const outlinePath = [
    `M${drawnX},${drawnY + drawnHeight}`,
    `V${drawnY + radius}`,
    `Q${drawnX},${drawnY} ${drawnX + radius},${drawnY}`,
    `H${drawnX + drawnWidth - radius}`,
    `Q${drawnX + drawnWidth},${drawnY} ${drawnX + drawnWidth},${drawnY + radius}`,
    `V${drawnY + drawnHeight}`,
  ].join(' ');

  return { fillPath, outlinePath };
};

export type VestingGraphBarShapeProps = {
  domainSpan: number;
  fill?: string;
  height?: number;
  index?: number;
  introDelayMs: number;
  introPhase: VestingGraphIntroPhase;
  nextVestIndex: null | number;
  payload?: VestingGraphSeriesPoint;
  plotWidth: number;
  width?: number;
  x?: number;
  y?: number;
};

export const resolveAllocationBarWidthPx = ({
  domainSpan,
  plotWidth,
  slotHalfWidth,
}: {
  domainSpan: number;
  plotWidth: number;
  slotHalfWidth: number;
}) => {
  if (slotHalfWidth <= 0 || domainSpan <= 0) return 0;

  const slotWidthPx = ((slotHalfWidth * 2) / domainSpan) * plotWidth;

  return Math.min(
    VESTING_GRAPH_MAX_BAR_SIZE_PX,
    Math.max(
      VESTING_GRAPH_MIN_BAR_SIZE_PX,
      Math.floor(slotWidthPx * VESTING_GRAPH_BAR_SLOT_FILL_RATIO),
    ),
  );
};

/**
 * Rounded top fill for every bar. Width follows the equal-column slot so neighbouring
 * payments stay the same size. The current / next-vest step also gets a blue dashed
 * outline on the sides and top only — the baseline stays clean against the X-axis.
 */
export const VestingGraphBarShape = ({
  domainSpan,
  fill = 'transparent',
  height = 0,
  index,
  introDelayMs,
  introPhase,
  nextVestIndex,
  payload,
  plotWidth,
  width = 0,
  x = 0,
  y = 0,
}: VestingGraphBarShapeProps) => {
  const drawnWidth = resolveAllocationBarWidthPx({
    domainSpan,
    plotWidth,
    slotHalfWidth: payload?.slotHalfWidth ?? 0,
  });
  // Recharts centres a uniform barSize on the datum; re-centre our slot width on it.
  const drawnX = x + width / 2 - drawnWidth / 2;
  const isVisible = height > 0 && drawnWidth > 0 && fill !== 'transparent';
  const introScale = useMotionValue(introPhase === 'done' ? 1 : 0);
  const layoutRef = useRef({ drawnWidth, drawnX, height, y });
  layoutRef.current = { drawnWidth, drawnX, height, y };

  useEffect(() => {
    if (introPhase === 'playing') {
      const controls = animate(introScale, 1, {
        delay: introDelayMs / 1000,
        duration: VESTING_GRAPH_INTRO_BAR_MS / 1000,
        ease: VESTING_GRAPH_INTRO_EASE,
      });

      return () => controls.stop();
    }

    introScale.set(introPhase === 'done' ? 1 : 0);
    return undefined;
  }, [introDelayMs, introPhase, introScale]);

  const fillD = useTransform(
    introScale,
    (scale) => buildVestingGraphBarPaths({ ...layoutRef.current, scale }).fillPath,
  );
  const outlineD = useTransform(
    introScale,
    (scale) => buildVestingGraphBarPaths({ ...layoutRef.current, scale }).outlinePath,
  );

  if (!isVisible) return null;

  const settledPaths = buildVestingGraphBarPaths({
    drawnWidth,
    drawnX,
    height,
    scale: 1,
    y,
  });
  const showOutline = index === nextVestIndex;

  if (introPhase === 'done') {
    return (
      <g>
        <path d={settledPaths.fillPath} fill={fill} />
        {showOutline && (
          <path
            d={settledPaths.outlinePath}
            fill="none"
            stroke={VESTING_GRAPH_VESTED_COLOR}
            strokeDasharray="2 2"
            strokeWidth={1}
          />
        )}
      </g>
    );
  }

  return (
    <g>
      <motion.path d={fillD} fill={fill} />
      {showOutline && (
        <motion.path
          d={outlineD}
          fill="none"
          stroke={VESTING_GRAPH_VESTED_COLOR}
          strokeDasharray="2 2"
          strokeWidth={1}
        />
      )}
    </g>
  );
};

const isSameChartColumn = (point: VestingGraphSeriesPoint, hoveredPoint: VestingGraphSeriesPoint) =>
  point.timestamp === hoveredPoint.timestamp &&
  point.calendarTimestamp === hoveredPoint.calendarTimestamp;

/**
 * Hovering a column (or a dynamic period) sorts the bars into three tones: the active
 * column/period comes forward, the rest of the vested run steps back, and with nothing
 * hovered they all read alike. Current / next-vest uses the same unvested wash.
 */
export const resolveBarFill = ({
  hoveredPeriod,
  hoveredPoint,
  point,
}: {
  hoveredPeriod: HoveredVestingPeriod | null;
  hoveredPoint: null | VestingGraphSeriesPoint;
  point: VestingGraphSeriesPoint;
}) => {
  const isHoveredColumn = hoveredPoint !== null && isSameChartColumn(point, hoveredPoint);
  const isInHoveredPeriod =
    hoveredPeriod !== null &&
    point.timestamp >= hoveredPeriod.startTimestamp &&
    point.timestamp <= hoveredPeriod.endTimestamp;
  const isHighlighted = isHoveredColumn || isInHoveredPeriod;

  if (point.status === VestingGraphPointStatus.VESTED) {
    if (isHighlighted) return VESTING_GRAPH_VESTED_ACTIVE_COLOR;

    return hoveredPeriod ? VESTING_GRAPH_VESTED_MUTED_COLOR : VESTING_GRAPH_VESTED_COLOR;
  }

  return isHighlighted ? VESTING_GRAPH_UNVESTED_ACTIVE_COLOR : VESTING_GRAPH_UNVESTED_COLOR;
};
